import { useContext, useEffect, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { getFeatureEntityLog } from "services/feature-apis";
import { ScRequestMaterial } from "../model";

/**
 * Minimal, read-only material list fetch for the SC-request view.
 *
 * This is a deliberately slim, self-contained duplicate of just the list-fetch
 * slice of `material-log-feature/hooks/material-feature-data.ts`. It does NOT
 * pull in column-config queries, save/delete mutations, master units, or the
 * change-event patching that the full material log needs — this page is purely
 * read-only.
 */
export const useScRequestMaterialData = () => {
  const {
    tokenRetrievalState,
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);

  // Start undefined (not []) and loading=true so the grid shows AG Grid's loading
  // overlay while the first fetch is in flight, rather than the "No data found"
  // (no-rows) overlay. CiqAgGrid force-clears `loading` when rowData is an empty
  // array, but not when it is undefined.
  const [entityList, setEntityList] = useState<ScRequestMaterial[] | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenRetrievalState?.token;
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let isActive = true;
    const fetchLogData = async () => {
      setLoading(true);
      try {
        const res = await getFeatureEntityLog({
          projectToken: token,
          featureType: FeatureTypes.MATERIALS
        });
        if (isActive && res.success) {
          setEntityList(res.data as ScRequestMaterial[]);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchLogData();

    return () => {
      isActive = false;
    };
  }, [tokenRetrievalState?.token]);

  return { entityList, loading, materialHeaderMap };
};
